// src/encounter/lib/note-line-delta.ts
//
// PROD-2961 (decision D1, option A) · frame v2 only. A note line (HPI / ROS / PE narrative or free text)
// is a PLAIN TEXT template answer (`valueString`), and every reader of it — the generated note, the
// Flutter PA, HIE builders, the A&P's read — keeps reading plain text. Beside it, a line that holds
// PIECES (a pick-list, a blank, a chart quote from a phrase) keeps its Delta in a `cps-note-delta`
// extension, so the pieces survive a save and a reload and stay editable.
//
// THE PLAIN TEXT IS DERIVED FROM THE DELTA, and the Delta is only trusted while it still derives the
// text that is stored (the A&P codec's checksum rule, note-pieces.ts §6.5): any writer that does not know
// pieces (a plain edit elsewhere, ambient, Flutter) changes the text, the Delta no longer matches, and
// the text wins — pieces are dropped, never a stale value kept as coded.
//
// THE DELTA'S SHAPE is the A&P note's WIRE shape: text runs, a piece being a run whose `cps-piece`
// attribute is the piece (note-pieces.ts header), ending in "\n". A piece run's text is its LINE display
// (`lineDisplay`, line-piece-text.ts): a pick-list or blank as in the A&P note, a chart link in the line's
// one-label, one-stamp wording — exactly the words the occurrence put in the line. Nothing here changes
// note-pieces.ts.
//
// The pieces are kept as they were COMPLETED: a pick-list's chosen codes, a blank's value, a chart link's
// snapshot with its "as of" time and each line's source record id (provenance). Nothing re-resolves them.
import type { DeltaOp } from './note-delta';
import { parseNoteOps } from './note-delta';
import { PIECE_ATTR, embeddedPiece, parsePiece, type Piece } from './note-pieces';
import { lineDisplay } from './line-piece-text';

/** One run of a line: plain text, or a piece (whose text is always its line display). */
export interface LineSeg {
  text: string;
  piece?: Piece;
}

/** The line's text: what `valueString` holds. */
export function segsText(segs: readonly LineSeg[]): string {
  return segs.map((s) => s.text).join('');
}

export function segsHavePieces(segs: readonly LineSeg[]): boolean {
  return segs.some((s) => s.piece);
}

/** Merge neighbouring plain runs and drop empty ones (a piece is never merged). */
function normalise(segs: readonly LineSeg[]): LineSeg[] {
  const out: LineSeg[] = [];
  for (const s of segs) {
    if (s.piece) {
      out.push(s);
      continue;
    }
    if (!s.text) continue;
    const last = out[out.length - 1];
    if (last && !last.piece) out[out.length - 1] = { text: last.text + s.text };
    else out.push({ text: s.text });
  }
  return out;
}

/** A stored `cps-note-delta` (wire ops JSON) → segments. A piece run whose text is not its own display
 *  (edited where pieces are unknown) becomes plain text. The terminal newline is not part of the line. */
export function deltaToSegs(deltaJson: string | null | undefined): LineSeg[] {
  const ops = parseNoteOps(deltaJson ?? '');
  const segs: LineSeg[] = [];
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (typeof op.insert !== 'string') continue;
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw === undefined) {
      segs.push({ text: op.insert });
      continue;
    }
    let text = op.insert;
    while (i + 1 < ops.length && ops[i + 1].attributes?.[PIECE_ATTR] === raw && typeof ops[i + 1].insert === 'string') {
      text += ops[++i].insert as string;
    }
    const piece = parsePiece(raw);
    segs.push(piece && text === lineDisplay(piece) ? { text, piece } : { text });
  }
  const joined = normalise(segs);
  const last = joined[joined.length - 1];
  if (last && !last.piece && last.text.endsWith('\n')) {
    const trimmed = last.text.slice(0, -1);
    return trimmed ? [...joined.slice(0, -1), { text: trimmed }] : joined.slice(0, -1);
  }
  return joined;
}

/** Segments → the stored `cps-note-delta` (wire ops JSON, ending in "\n"). */
export function segsToDelta(segs: readonly LineSeg[]): string {
  const ops: DeltaOp[] = normalise(segs).map((s) =>
    s.piece ? { insert: lineDisplay(s.piece), attributes: { [PIECE_ATTR]: JSON.stringify(s.piece) } } : { insert: s.text },
  );
  ops.push({ insert: '\n' });
  return JSON.stringify(ops);
}

/**
 * The line's segments, if `deltaJson` still derives `value` and holds a piece; otherwise undefined (no
 * Delta, an unreadable one, one without pieces, or a stale one: the plain text wins).
 */
export function lineSegs(deltaJson: string | null | undefined, value: unknown): LineSeg[] | undefined {
  if (!deltaJson || typeof value !== 'string') return undefined;
  const segs = deltaToSegs(deltaJson);
  return segsHavePieces(segs) && segsText(segs) === value ? segs : undefined;
}

/**
 * The line after a PLAIN edit of its text (the clinician typing in the field). The edited span is the
 * part between the longest common prefix and suffix; every piece outside it is kept, and a piece the
 * edit touches becomes plain text (it was edited as text, the checksum rule).
 */
export function rebaseSegs(segs: readonly LineSeg[], next: string): LineSeg[] {
  const prev = segsText(segs);
  if (prev === next) return [...segs];
  let start = 0;
  while (start < prev.length && start < next.length && prev[start] === next[start]) start++;
  let tail = 0;
  while (tail < prev.length - start && tail < next.length - start && prev[prev.length - 1 - tail] === next[next.length - 1 - tail]) tail++;
  const end = prev.length - tail; // the old span [start, end) became `inserted`
  const inserted = next.slice(start, next.length - tail);
  const out: LineSeg[] = [];
  let placed = false;
  const place = () => {
    if (!placed) out.push({ text: inserted });
    placed = true;
  };
  let at = 0;
  for (const s of segs) {
    const from = at;
    const to = at + s.text.length;
    at = to;
    if (to <= start) {
      out.push(s); // wholly before the edit
      continue;
    }
    if (from >= end) {
      place(); // wholly after the edit (an insertion at a run's edge leaves the run whole)
      out.push(s);
      continue;
    }
    // The edit reaches into this run: what is outside the span stays as PLAIN text (a piece edited as
    // text is text), the inserted text goes in once.
    if (start > from) out.push({ text: s.text.slice(0, start - from) });
    place();
    if (end < to) out.push({ text: s.text.slice(end - from) });
  }
  place();
  return normalise(out);
}

/**
 * Append a COMPLETED phrase (the occurrence's ops: text and filled occurrence embeds) at the end of the
 * line, with the block Macros' separator rule (`insertAt`): nothing after a trailing space, else a new line.
 */
export function appendPhrase(segs: readonly LineSeg[], phraseOps: readonly DeltaOp[]): LineSeg[] {
  const text = segsText(segs);
  const sep = text && !/\s$/.test(text) ? '\n' : '';
  return normalise([...segs, { text: sep }, ...phraseSegs(phraseOps)]);
}

/** A completed phrase's runs: its pieces as their line display, its text as is, without its terminal newline. */
function phraseSegs(phraseOps: readonly DeltaOp[]): LineSeg[] {
  const added: LineSeg[] = [];
  for (const op of phraseOps) {
    const piece = embeddedPiece(op);
    if (piece) added.push({ text: lineDisplay(piece), piece });
    else if (typeof op.insert === 'string') added.push({ text: op.insert });
  }
  // The phrase's own terminal newline is not part of the line.
  const last = added[added.length - 1];
  if (last && !last.piece && last.text.endsWith('\n')) last.text = last.text.replace(/\n+$/, '');
  return added;
}

/** PROD-3148: a completed phrase put where its `.` trigger was typed (`at`, the trigger's offset in the line's text),
 *  in place of nothing (NoteFieldLine already removed the trigger). A position inside a piece moves to the piece's end (a
 *  piece is never split). A position AT the end is the same: the phrase goes there, on the same line, for a typed `.`
 *  and a cursor alike (PROD-3166, Andrew's review of #671 and #672: one rule for every path); a position past the end is
 *  the end. Only no position (the block's Macros with no cursor) is an append on its own line (`appendPhrase`). */
export function insertPhrase(segs: readonly LineSeg[], phraseOps: readonly DeltaOp[], at?: number, pad = false): LineSeg[] {
  const text = segsText(segs);
  if (at === undefined) return appendPhrase(segs, phraseOps);
  // Where the phrase actually lands: a place inside a piece moves to the piece's end (a piece is never split); past the
  // end is the end.
  const place = landingOffset(segs, Math.max(0, Math.min(at, text.length)));
  // A space where a word would touch a word (Codex P3: the phrase's own edges count too), judged where the phrase LANDS
  // (PROD-3176). At a cursor (the block's Macros, PROD-3149) on both sides; for a typed `.` only at the end, after a word
  // whose space was lost (PROD-3166). `spliceAt` in note-phrases.ts applies the same rule to a line without Deltas.
  const phrase = phraseSegs(phraseOps);
  const words = segsText(phrase);
  const left = (pad || place === text.length) && place > 0 && !/\s/.test(text[place - 1]) && !/^\s/.test(words);
  const added = [
    ...(left ? [{ text: ' ' }] : []),
    ...phrase,
    ...(pad && place < text.length && !/\s/.test(text[place]) && !/\s$/.test(words) ? [{ text: ' ' }] : []),
  ];
  const out: LineSeg[] = [];
  let pos = 0;
  let placed = false;
  for (const s of segs) {
    const end = pos + s.text.length;
    if (!placed && place < end) {
      const offset = Math.max(0, place - pos);
      if (s.piece) out.push(...(offset === 0 ? [...added, s] : [s, ...added]));
      else out.push({ text: s.text.slice(0, offset) }, ...added, { text: s.text.slice(offset) });
      placed = true;
    } else out.push(s);
    pos = end;
  }
  return normalise(placed ? out : [...out, ...added]);
}

/** The offset a phrase placed at `at` really lands on: inside a piece, the piece's end; at a piece's start, as is. */
function landingOffset(segs: readonly LineSeg[], at: number): number {
  let pos = 0;
  for (const s of segs) {
    const end = pos + s.text.length;
    if (at < end) return s.piece && at > pos ? end : at;
    pos = end;
  }
  return at;
}

/** A plain line's text as one segment (the start for a phrase appended to a line with no pieces yet). */
export const plainSegs = (text: string): LineSeg[] => (text ? [{ text }] : []);
